import React, { useState, useEffect } from 'react';
import { 
  Typography, Box, Container, Grid, Paper, TextField, Button, Accordion, 
  AccordionSummary, AccordionDetails, CircularProgress, Fade, Zoom, Avatar,
  useTheme, Card, CardContent, Divider, Chip, InputAdornment, IconButton
} from '@mui/material';
import { 
  Search, Mail, Phone, Chat, ExpandMore, Info, LiveHelp, ContactSupport,
  QuestionAnswer, HelpOutline, Send, AccessTime, ArrowForward, RestaurantMenu,
  AccountBalance, DevicesOther
} from '@mui/icons-material';

const CustomerSupport = () => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);
  
  // Simulating loading effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const faqs = [
    {
      question: 'Can I change my nutritionist?',
      answer: 'Yes, you can request a change of nutritionist from your Profile page. Go to Settings and select "Change Nutritionist" to browse available options.'
    },
    {
      question: 'How do I pause my program if I\'m on vacation?',
      answer: 'You can pause your subscription for up to 30 days by going to your Profile settings and selecting "Pause Subscription". You can specify the date range for your pause.'
    },
    {
      question: 'What happens when my subscription expires?',
      answer: 'When your subscription expires, you\'ll receive a notification 7 days prior. You can choose to renew, cancel, or upgrade your plan at any time from your Profile settings.'
    },
    {
      question: 'Can I connect other health apps to GeoDiet?',
      answer: 'Yes, you can connect compatible health apps like Fitbit, Apple Health, and others through the Integrations section in your Profile settings.'
    },
    {
      question: 'How do I update my payment information?',
      answer: 'Payment information can be updated in your Profile under the Billing section. Click on "Payment Methods" to add, remove, or update your payment details.'
    }
  ];

  const supportCategories = [
    { title: 'Account Help', icon: <Info color="primary" />, count: 15 },
    { title: 'Meal Plans', icon: <RestaurantMenu sx={{ color: '#4caf50' }} />, count: 23 },
    { title: 'Payments & Billing', icon: <AccountBalance sx={{ color: '#ff9800' }} />, count: 18 },
    { title: 'Technical Support', icon: <DevicesOther sx={{ color: '#9c27b0' }} />, count: 12 }
  ];

  const handleSectionHover = (section) => {
    setActiveSection(section);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#F2F5F8'
      }}>
        <CircularProgress sx={{ color: '#291759' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      background: '#F2F5F8', 
      minHeight: '100vh', 
      py: 4,
      overflow: 'hidden'
    }}>
      {/* Hero Section */}
      <Container maxWidth="lg">
        <Fade in={!loading} timeout={800}>
          <Box sx={{ 
            position: 'relative',
            px: 5, 
            py: 8,
            mb: 6,
            borderRadius: 4,
            background: `linear-gradient(135deg, #291759 0%, #1A0F3C 100%)`,
            color: 'white',
            overflow: 'hidden',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }}>
            {/* Decorative shapes */}
            <Box sx={{
              position: 'absolute',
              top: -20,
              right: -20,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)',
              zIndex: 0
            }} />
            <Box sx={{
              position: 'absolute',
              bottom: -30,
              right: 100,
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)',
              zIndex: 0
            }} />
            
            <Box sx={{ position: 'relative', zIndex: 2, maxWidth: 600 }}>
              <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 2, fontWeight: 500 }}>
                CUSTOMER SUPPORT CENTER
              </Typography>
              <Typography variant="h2" fontWeight="bold" sx={{ mb: 2 }}>
                How can we help you today?
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, fontWeight: 400, opacity: 0.9 }}>
                Get quick solutions to your questions about GeoDiet
              </Typography>
              
              <Box sx={{ 
                display: 'flex',
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 5px 20px rgba(0,0,0,0.15)'
              }}>
                <TextField
                  fullWidth
                  placeholder="Search for answers..."
                  variant="outlined"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                    sx: { 
                      bgcolor: 'white', 
                      py: 0.5,
                      '&.MuiOutlinedInput-root': {
                        '& fieldset': { border: 'none' },
                      }
                    }
                  }}
                />
                <Button 
                  variant="contained" 
                  disableElevation
                  sx={{ 
                    bgcolor: '#fff',
                    color: '#291759',
                    px: 4,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                    }
                  }}
                >
                  Search
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>

        {/* Support Options */}
        <Typography variant="h4" fontWeight="600" sx={{ mb: 4 }}>
          Contact Support
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {/* Live Chat Support */}
          <Grid item xs={12} md={4}>
            <Zoom in={!loading} style={{ transitionDelay: '200ms' }}>
              <Card sx={{ 
                height: '100%',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                overflow: 'hidden',
                boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 20px 25px -5px, rgba(0, 0, 0, 0.04) 0px 10px 10px -5px'
                }
              }}>
                <Box sx={{ 
                  height: 8, 
                  bgcolor: '#2196f3' 
                }} />
                <CardContent sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  p: 4
                }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: 'rgba(33, 150, 243, 0.1)',
                      color: '#2196f3',
                      mb: 3
                    }}
                  >
                    <Chat fontSize="large" />
                  </Avatar>
                  
                  <Typography variant="h5" fontWeight="600" gutterBottom>Live Chat</Typography>
                  <Chip 
                    label="Available Now" 
                    size="small" 
                    sx={{ 
                      bgcolor: 'rgba(76, 175, 80, 0.1)', 
                      color: '#4caf50',
                      fontWeight: 600,
                      mb: 2
                    }} 
                  />
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 1 }}>
                    <AccessTime fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">Average wait: 2 minutes</Typography>
                  </Box>
                  
                  <Button 
                    variant="contained" 
                    fullWidth
                    size="large"
                    sx={{ 
                      py: 1.5,
                      borderRadius: 2,
                      bgcolor: '#2196f3',
                      boxShadow: 'rgba(33, 150, 243, 0.24) 0px 8px 16px 0px',
                      '&:hover': {
                        bgcolor: '#1976d2'
                      }
                    }}
                    endIcon={<ArrowForward />}
                  >
                    Start Chat
                  </Button>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
          
          {/* Email Support */}
          <Grid item xs={12} md={4}>
            <Zoom in={!loading} style={{ transitionDelay: '300ms' }}>
              <Card sx={{ 
                height: '100%',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                overflow: 'hidden',
                boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 20px 25px -5px, rgba(0, 0, 0, 0.04) 0px 10px 10px -5px'
                }
              }}>
                <Box sx={{ 
                  height: 8, 
                  bgcolor: '#673ab7' 
                }} />
                <CardContent sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  p: 4
                }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: 'rgba(103, 58, 183, 0.1)',
                      color: '#673ab7',
                      mb: 3
                    }}
                  >
                    <Mail fontSize="large" />
                  </Avatar>
                  
                  <Typography variant="h5" fontWeight="600" gutterBottom>Email Support</Typography>
                  <Typography variant="body1" fontWeight="500" sx={{ mb: 2 }}>
                    support@wonder.health
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 1 }}>
                    <AccessTime fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">Response within 24 hours</Typography>
                  </Box>
                  
                  <Button 
                    variant="outlined" 
                    fullWidth
                    size="large"
                    sx={{ 
                      py: 1.5,
                      borderRadius: 2,
                      color: '#673ab7',
                      borderColor: '#673ab7',
                      '&:hover': {
                        borderColor: '#5e35b1',
                        bgcolor: 'rgba(103, 58, 183, 0.04)'
                      }
                    }}
                    endIcon={<Send />}
                  >
                    Send Email
                  </Button>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
          
          {/* Phone Support */}
          <Grid item xs={12} md={4}>
            <Zoom in={!loading} style={{ transitionDelay: '400ms' }}>
              <Card sx={{ 
                height: '100%',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                overflow: 'hidden',
                boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 20px 25px -5px, rgba(0, 0, 0, 0.04) 0px 10px 10px -5px'
                }
              }}>
                <Box sx={{ 
                  height: 8, 
                  bgcolor: '#ff9800' 
                }} />
                <CardContent sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  p: 4
                }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: 'rgba(255, 152, 0, 0.1)',
                      color: '#ff9800',
                      mb: 3
                    }}
                  >
                    <Phone fontSize="large" />
                  </Avatar>
                  
                  <Typography variant="h5" fontWeight="600" gutterBottom>Phone Support</Typography>
                  <Typography variant="body1" fontWeight="500" sx={{ mb: 2 }}>
                    +1 (800) 123-4567
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 1 }}>
                    <AccessTime fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">Mon-Fri, 9AM-5PM ET</Typography>
                  </Box>
                  
                  <Button 
                    variant="outlined" 
                    fullWidth
                    size="large"
                    sx={{ 
                      py: 1.5,
                      borderRadius: 2,
                      color: '#ff9800',
                      borderColor: '#ff9800',
                      '&:hover': {
                        borderColor: '#f57f17',
                        bgcolor: 'rgba(255, 152, 0, 0.04)'
                      }
                    }}
                    endIcon={<Phone />}
                  >
                    Call Now
                  </Button>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
        </Grid>

        {/* Help Categories */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" fontWeight="600" sx={{ mb: 4 }}>
            Help Categories
          </Typography>
          
          <Grid container spacing={2}>
            {supportCategories.map((category, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Fade in={!loading} style={{ transitionDelay: `${500 + index * 100}ms` }}>
                  <Card sx={{ 
                    p: 3,
                    borderRadius: 3,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
                    '&:hover': {
                      boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px',
                      transform: 'translateY(-4px)'
                    }
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: 'rgba(0, 0, 0, 0.04)'
                          }}
                        >
                          {category.icon}
                        </Avatar>
                        <Typography variant="h6" fontWeight="500">{category.title}</Typography>
                      </Box>
                      <Chip 
                        label={category.count} 
                        size="small" 
                        sx={{ bgcolor: 'rgba(0, 0, 0, 0.04)' }} 
                      />
                    </Box>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Box>
        
        {/* FAQ Section */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
            <Avatar
              sx={{
                width: 50,
                height: 50,
                bgcolor: 'rgba(41, 23, 89, 0.1)',
                color: '#291759'
              }}
            >
              <QuestionAnswer />
            </Avatar>
            <Typography variant="h4" fontWeight="600">
              Frequently Asked Questions
            </Typography>
          </Box>
          
          <Box sx={{ mb: 1, maxWidth: 700 }}>
            <Typography variant="body1" color="text.secondary" paragraph>
              Find answers to common questions about your GeoDiet program
            </Typography>
            <Divider sx={{ mb: 3 }} />
          </Box>
          
          <Box sx={{ maxWidth: 900 }}>
            {faqs.map((faq, index) => (
              <Accordion 
                key={index} 
                sx={{ 
                  mb: 1.5, 
                  boxShadow: 'none', 
                  border: '1px solid', 
                  borderColor: 'rgba(0, 0, 0, 0.08)',
                  borderRadius: '12px !important',
                  overflow: 'hidden',
                  '&:before': {
                    display: 'none'
                  }
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMore />}
                  sx={{ 
                    px: 3,
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.02)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <HelpOutline color="primary" fontSize="small" />
                    <Typography fontWeight={500}>{faq.question}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                  <Typography color="text.secondary">{faq.answer}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
        
        {/* Still Need Help */}
        <Fade in={!loading} style={{ transitionDelay: '900ms' }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 4,
            borderRadius: 3,
            bgcolor: 'rgba(0, 0, 0, 0.02)',
            border: '1px dashed rgba(0, 0, 0, 0.1)',
            mb: 4
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 3, md: 0 }, gap: 2 }}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: 'rgba(41, 23, 89, 0.1)',
                  color: '#291759'
                }}
              >
                <ContactSupport fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="600" gutterBottom>
                  Still need help?
                </Typography>
                <Typography color="text.secondary">
                  Our support team is ready to assist you with any questions
                </Typography>
              </Box>
            </Box>
            
            <Button 
              variant="contained"
              size="large"
              startIcon={<LiveHelp />}
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                bgcolor: '#291759',
                '&:hover': {
                  bgcolor: '#1A0F3C'
                }
              }}
            >
              Contact Support
            </Button>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default CustomerSupport; 